import React, { Suspense, useCallback } from 'react';
import Spinner from '@medflex/components/stories/Spinner';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import { useTranslation } from 'react-i18next';
import { FEATURE_KEYS, SUBSCRIPTION_PLANS } from '@medflex/utils/subscriptionEnums';
import { Switch, SwitchStatusLabel } from '@medflex/components/stories/Switch';
import {
  ArrowRightCircleIcon,
  MinusCircleIcon,
  UserCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import Badge, { KINDS, SIZES } from '@medflex/components/stories/Badge';
import useUserProfile from '../../../hooks/useUserProfile';
import ErrorPage from '../../../components/ErrorPage';
import useModal from '../../../hooks/useModal';
import { MODALS } from '../../../hooks/useIsModal';
import useFeatures from '../../../hooks/useFeatures';
import AbsenceModule from './Absence/AbsenceModule';
import useSubscription from '../../../hooks/useSubscription';
import RequestTagsModule from './RequestTags/RequestTagsModule';
import PhoneOpeningHoursModule from './PhoneOpeningHours/PhoneOpeningHoursModule';

// eslint-disable-next-line import/no-unresolved
const SettingsBasic = React.lazy(() => import('RemoteWidget/SettingsBasic'));

const InquiryManagementModule = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'InquiryManagementModule' });
  const { t: tS } = useTranslation('physician', { keyPrefix: 'PhysicianSubscriptionModule' });
  const { userProfile, editContactPage } = useUserProfile();
  const subscription = useSubscription();
  const [pamFeature, pamPhoneAssistant] = useFeatures([
    FEATURE_KEYS.PAM,
    FEATURE_KEYS.PAM_PHONE_ASSISTANT,
  ]);
  const settings = userProfile?.attributes.pages && userProfile.attributes.pages.widgetSettings;
  const patientRegistrationRequired =
    userProfile?.attributes.pages && userProfile.attributes.pages.patientRegistrationRequired;
  const inquiryManagementPhoneSettings =
    userProfile?.attributes?.pages?.inquiryManagementSettings?.phone;

  const { open } = useModal();

  const handleEditMode = useCallback(() => {
    open({ modal: MODALS.physicianPagesWidgetSettings });
  }, [open]);

  const handleChange = useCallback(
    widgetSettings => {
      editContactPage({ widgetSettings });
    },
    [editContactPage],
  );

  const handleInquirySettingInviteBySms = useCallback(
    checked => {
      editContactPage({
        inquiryManagementSettings: {
          phone: {
            inviteBySms: checked,
          },
        },
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'switch | toggled',
        eventName: `phoneAssistant inviteBySms option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [editContactPage],
  );

  const handlePatientRegistrationRequired = useCallback(
    checked => {
      editContactPage({
        patientRegistrationRequired: checked,
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'checkbox | checked',
        eventName: `register option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [editContactPage],
  );

  const handlePhoneStatusFiltersChanged = useCallback(
    (fieldName, checked) => {
      editContactPage({
        inquiryManagementSettings: {
          phone: {
            [fieldName]: checked ? new Date().toISOString() : null,
          },
        },
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'switch | toggled',
        eventName: `${fieldName} option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [editContactPage],
  );

  const handleForwardedCallsFilterChanged = useCallback(
    checked => {
      editContactPage({
        inquiryManagementSettings: {
          phone: {
            forwardedCalls: {
              activatedAt: checked ? new Date().toISOString() : null,
            },
          },
        },
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'switch | toggled',
        eventName: `forwardedCalls option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [editContactPage],
  );

  if (!settings) {
    return null;
  }

  return (
    <div>
      <div>
        <h3 className="mb-5 text-xl font-semibold">
          {t('headline')}{' '}
          {!pamFeature.active && (
            <Badge className="ml-2" kind={KINDS.SIGNAL} size={SIZES.SMALL}>
              {tS('upgrade')}
            </Badge>
          )}
        </h3>
        <div className="mb-5">{t('headline_subtitle')}</div>
      </div>
      <hr className="border-t-1 mb-5" />
      <h3 className="mb-5 text-xl font-semibold">{t('title')}</h3>
      <RequestTagsModule />
      <SettingsBox className="mb-6">
        <Suspense
          fallback={
            <div className="flex w-full items-center justify-center">
              <Spinner size="3xl" />
            </div>
          }
        >
          <ErrorPage className="h-auto w-full" userType={userProfile?.type}>
            <SettingsBasic
              disabled={!pamFeature.active}
              settings={settings}
              onChange={handleChange}
              onEditMode={handleEditMode}
            >
              <label
                className="flex space-x-3"
                htmlFor="InquiryManagementModule_patientRegistrationRequired"
              >
                <span>
                  <UserCircleIcon className="h-8 w-8 stroke-1" />
                </span>
                <div>
                  <div className="font-semibold">{t('patientRegistrationRequired.title')}</div>
                  <div className="text-sm text-slate-600">
                    {t('patientRegistrationRequired.label')}
                  </div>
                </div>
                <Switch
                  id="InquiryManagementModule_patientRegistrationRequired"
                  checked={patientRegistrationRequired}
                  disabled={!pamFeature.active}
                  label={t('patientRegistrationRequired.label')}
                  onChange={handlePatientRegistrationRequired}
                  statusLabel={
                    <SwitchStatusLabel
                      enabled={patientRegistrationRequired}
                      className="hidden lg:inline"
                    />
                  }
                />
              </label>
            </SettingsBasic>
          </ErrorPage>
        </Suspense>
      </SettingsBox>
      {pamFeature.active && pamPhoneAssistant.active && inquiryManagementPhoneSettings && (
        <div className="mb-6 flex flex-col gap-5">
          <h3 className="text-xl font-semibold">{t('phone.headline')}</h3>
          <SettingsBox>
            <label
              className="flex space-x-2"
              htmlFor="InquiryManagementModule_inquiryPhoneSettings_smsInvite"
            >
              <span>
                <UserCircleIcon className="h-8 w-8 stroke-1" />
              </span>
              <div>
                <div className="font-semibold">{t('phone.inviteBySms.title')}</div>
                <div className="text-sm text-slate-600 p-2 pl-0">
                  {t('phone.inviteBySms.label')}
                </div>
              </div>
              <Switch
                id="InquiryManagementModule_inquiryPhoneSettings_smsInvite"
                checked={inquiryManagementPhoneSettings.inviteBySms}
                label={t('phone.inviteBySms.label')}
                onChange={handleInquirySettingInviteBySms}
                statusLabel={
                  <SwitchStatusLabel
                    enabled={inquiryManagementPhoneSettings.inviteBySms}
                    className="hidden lg:inline"
                  />
                }
              />
            </label>
          </SettingsBox>

          <SettingsBox>
            <div className="flex flex-col gap-4">
              <label
                className="flex space-x-2"
                htmlFor="InquiryManagementModule_inquiryPhoneSettings_uncompleted"
              >
                <span>
                  <MinusCircleIcon className="h-8 w-8 stroke-1" />
                </span>
                <div className="w-full">
                  <div className="font-semibold">{t('phone.uncompletedCalls.title')}</div>
                  <div className="text-sm text-slate-600 p-2 pl-0">
                    {t('phone.uncompletedCalls.label')}
                  </div>
                </div>
                <Switch
                  id="InquiryManagementModule_inquiryPhoneSettings_uncompleted"
                  checked={!!inquiryManagementPhoneSettings.uncompletedCallsAt}
                  label={t('phone.uncompletedCalls.label')}
                  onChange={checked =>
                    handlePhoneStatusFiltersChanged('uncompletedCallsAt', checked)
                  }
                  statusLabel={
                    <SwitchStatusLabel
                      enabled={!!inquiryManagementPhoneSettings.uncompletedCallsAt}
                      className="hidden lg:inline"
                    />
                  }
                />
              </label>

              <label
                className="flex space-x-2"
                htmlFor="InquiryManagementModule_inquiryPhoneSettings_canceled"
              >
                <span>
                  <XCircleIcon className="h-8 w-8 stroke-1" />
                </span>
                <div className="w-full">
                  <div className="font-semibold">{t('phone.canceledCalls.title')}</div>
                  <div className="text-sm text-slate-600 p-2 pl-0">
                    {t('phone.canceledCalls.label')}
                  </div>
                </div>
                <Switch
                  id="InquiryManagementModule_inquiryPhoneSettings_canceled"
                  checked={!!inquiryManagementPhoneSettings.canceledCallsAt}
                  label={t('phone.canceledCalls.label')}
                  onChange={checked => handlePhoneStatusFiltersChanged('canceledCallsAt', checked)}
                  statusLabel={
                    <SwitchStatusLabel
                      enabled={!!inquiryManagementPhoneSettings.canceledCallsAt}
                      className="hidden lg:inline"
                    />
                  }
                />
              </label>

              <label
                className="flex space-x-2"
                htmlFor="InquiryManagementModule_inquiryPhoneSettings_forwarded"
              >
                <span>
                  <ArrowRightCircleIcon className="h-8 w-8 stroke-1" />
                </span>
                <div className="w-full">
                  <div className="font-semibold">{t('phone.forwardedCalls.title')}</div>
                  <div className="text-sm text-slate-600 p-2 pl-0">
                    {t('phone.forwardedCalls.label')}
                  </div>
                </div>
                <Switch
                  id="InquiryManagementModule_inquiryPhoneSettings_forwarded"
                  checked={!!inquiryManagementPhoneSettings.forwardedCalls?.activatedAt}
                  label={t('phone.forwardedCalls.label')}
                  onChange={handleForwardedCallsFilterChanged}
                  statusLabel={
                    <SwitchStatusLabel
                      enabled={!!inquiryManagementPhoneSettings.forwardedCalls?.activatedAt}
                      className="hidden lg:inline"
                    />
                  }
                />
              </label>
            </div>
          </SettingsBox>

          <PhoneOpeningHoursModule />
        </div>
      )}
      {![SUBSCRIPTION_PLANS.BASIC, SUBSCRIPTION_PLANS.STARTERSESSION].includes(
        subscription.plan,
      ) && <AbsenceModule />}
    </div>
  );
};

export default InquiryManagementModule;
